import React, { useState, useEffect, useRef, useCallback } from "react";
import { faPencil, faTrash, faPlay, faPause, faPlus, faTimes, faEllipsisV, faClock, faChevronRight, faChevronDown, faArrowRight, faCheck, faForwardStep, faVolumeHigh, faVolumeXmark, faMusic, faRotateLeft, faChartColumn, faInfinity, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionMenu from "../action-menu/ActionMenu";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import { generateId } from "../../utils/Utils";
import { TimePicker } from "../time-picker/TimePicker";
import ListSelectionDialog from "../list-selection-dialog/ListSelectionDialog";
import { Timer, Collection } from "../../types";
import { useTranslation } from "react-i18next";

import "./TimerElement.css";

interface TimerElementProps {
    element: Timer;
    deleteElement: (id: string) => void;
    updateElement: (updatedElement: Timer) => void;
    lists: Collection[];
    selectedListId: string;
    moveElementToList: (element: Timer, listId: string) => void;
    onMoveToSection?: (element: Timer) => void;
}

export const TimerElement: React.FC<TimerElementProps> = ({
    element,
    deleteElement,
    updateElement,
    lists,
    selectedListId,
    moveElementToList,
    onMoveToSection,
}) => {
    const { t, i18n } = useTranslation();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showStatsDialog, setShowStatsDialog] = useState(false);
    const [statsView, setStatsView] = useState<'overview' | 'sessions'>('overview');
    const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
    const [showListSelectionDialog, setShowListSelectionDialog] = useState(false);
    const [editTitle, setEditTitle] = useState(element.title || '');
    const [editMode, setEditMode] = useState<'simple' | 'steps'>(element.mode === 'simple' ? 'simple' : 'steps');
    const [editSubtimers, setEditSubtimers] = useState(element.subtimers || []);
    const [newSubtimer, setNewSubtimer] = useState('');
    const [newTargetTime, setNewTargetTime] = useState(60);
    const [subtimersCollapsed, setSubtimersCollapsed] = useState(true);

    const [isPlaying, setIsPlaying] = useState(false);
    const [hasStartedCurrentStep, setHasStartedCurrentStep] = useState(false);
    const [currentSubtimerIndex, setCurrentSubtimerIndex] = useState(0);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [pausedTime, setPausedTime] = useState(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
    const handleSubtimerCompleteRef = useRef<((recordedElapsedTime?: number, completionMethod?: 'automatic' | 'manual') => void) | null>(null);
    const sessionStartedAtRef = useRef<string | null>(null);
    const sessionPausedTimeRef = useRef(0);
    const sessionStepRecordsRef = useRef<Record<string, { actualTime: number; completionMethod: 'automatic' | 'manual' }>>({});
    const announcedRemainingMilestonesRef = useRef<Set<number>>(new Set());
    const announcedOvertimeMinutesRef = useRef<Set<number>>(new Set());
    const audioContextRef = useRef<AudioContext | null>(null);
    const speechQueueRef = useRef<string[]>([]);
    const isSpeakingRef = useRef(false);
    const activeUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
    const speechWatchdogTimerRef = useRef<NodeJS.Timeout | null>(null);
    const fallbackToneTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [speechVoices, setSpeechVoices] = useState<SpeechSynthesisVoice[]>([]);

    const subtimers = element.subtimers || [];
    const isSimpleMode = element.mode === 'simple';
    const currentSubtimer = subtimers[currentSubtimerIndex];
    const targetTime = currentSubtimer?.targetTime || 60;
    const remainingTime = Math.max(targetTime - elapsedTime, 0);
    const overtime = Math.max(elapsedTime - targetTime, 0);
    const displayTime = overtime > 0 ? overtime : remainingTime;
    const totalTargetTime = subtimers.reduce((total, subtimer) => total + (subtimer.targetTime || 0), 0);
    const completedBeforeCurrent = subtimers
        .slice(0, currentSubtimerIndex)
        .reduce((total, subtimer) => total + (subtimer.targetTime || 0), 0);
    const routineElapsedTime = Math.min(completedBeforeCurrent + Math.min(elapsedTime, targetTime), totalTargetTime);
    const routineProgress = totalTargetTime > 0 ? Math.round((routineElapsedTime / totalTargetTime) * 100) : 0;
    const completedCount = subtimers.filter((subtimer, index) => subtimer.completed || index < currentSubtimerIndex).length;
    const isRoutineComplete = subtimers.length > 0 && subtimers.every((subtimer) => subtimer.completed);
    const getSubtimerAutoMove = (subtimer = currentSubtimer) => {
        if (!subtimer) return true;
        if (typeof subtimer.autoMoveNext === 'boolean') return subtimer.autoMoveNext;
        return !element.countOvertime;
    };
    const shouldAutoMoveCurrentSubtimer = getSubtimerAutoMove(currentSubtimer);
    const shouldCountOvertime = !shouldAutoMoveCurrentSubtimer;
    const isVoiceEnabled = element.voiceEnabled !== false;
    const isSoundEnabled = element.soundEnabled !== false;

    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

        const loadVoices = () => {
            setSpeechVoices(window.speechSynthesis.getVoices());
        };

        loadVoices();
        window.speechSynthesis.addEventListener?.('voiceschanged', loadVoices);

        return () => {
            window.speechSynthesis?.removeEventListener?.('voiceschanged', loadVoices);
        };
    }, []);

    const playNotificationTone = useCallback((tone: 'countdown' | 'complete' | 'overtime' = 'complete') => {
        if (typeof window === 'undefined') return;

        const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextConstructor) return;

        try {
            const audioContext = audioContextRef.current || new AudioContextConstructor();
            audioContextRef.current = audioContext;

            const patterns = {
                countdown: [{ frequency: 660, start: 0, duration: 0.16 }],
                complete: [
                    { frequency: 660, start: 0, duration: 0.16 },
                    { frequency: 990, start: 0.2, duration: 0.28 },
                ],
                overtime: [
                    { frequency: 440, start: 0, duration: 0.22 },
                    { frequency: 330, start: 0.26, duration: 0.32 },
                ],
            } as const;

            patterns[tone].forEach(({ frequency, start, duration }) => {
                const oscillator = audioContext.createOscillator();
                const gain = audioContext.createGain();
                const startTime = audioContext.currentTime + start;
                oscillator.type = 'sine';
                oscillator.frequency.setValueAtTime(frequency, startTime);
                gain.gain.setValueAtTime(0.001, startTime);
                gain.gain.exponentialRampToValueAtTime(0.16, startTime + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
                oscillator.connect(gain);
                gain.connect(audioContext.destination);
                oscillator.start(startTime);
                oscillator.stop(startTime + duration + 0.02);
            });
        } catch {
            // Some browsers block audio until interaction; speech still covers supported cases.
        }
    }, []);

    const getSpeechLanguage = useCallback(() => {
        return i18n.language?.startsWith('ro') ? 'ro-RO' : 'en-US';
    }, [i18n.language]);

    const getPreferredVoice = useCallback((language: string) => {
        const baseLanguage = language.split('-')[0];
        const availableVoices = speechVoices.length > 0
            ? speechVoices
            : window.speechSynthesis?.getVoices?.() || [];
        const matchingVoices = availableVoices.filter((voice) =>
            voice.lang.toLowerCase().startsWith(baseLanguage.toLowerCase())
        );

        const scoreVoice = (voice: SpeechSynthesisVoice) => {
            const name = voice.name.toLowerCase();
            let score = voice.lang.toLowerCase() === language.toLowerCase() ? 100 : 0;
            if (name.includes('natural')) score += 60;
            if (name.includes('online')) score += 45;
            if (name.includes('google')) score += 35;
            if (name.includes('microsoft')) score += 25;
            if (voice.default) score += 5;
            if (name.includes('espeak') || name.includes('compact') || name.includes('basic')) score -= 80;
            return score;
        };

        return [...matchingVoices].sort((a, b) => scoreVoice(b) - scoreVoice(a))[0] || null;
    }, [speechVoices]);

    const speakNextAnnouncement = useCallback(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) return false;
        if (isSpeakingRef.current) return true;

        const message = speechQueueRef.current.shift();
        if (!message) return true;

        try {
            const speechLanguage = getSpeechLanguage();
            const utterance = new SpeechSynthesisUtterance(message);
            const preferredVoice = getPreferredVoice(speechLanguage);
            utterance.lang = speechLanguage;
            if (preferredVoice) utterance.voice = preferredVoice;
            utterance.rate = speechLanguage === 'ro-RO' ? 0.86 : 0.92;
            utterance.pitch = 1;
            utterance.volume = 1;

            const finishCurrentAnnouncement = () => {
                if (speechWatchdogTimerRef.current) {
                    clearTimeout(speechWatchdogTimerRef.current);
                    speechWatchdogTimerRef.current = null;
                }
                activeUtteranceRef.current = null;
                isSpeakingRef.current = false;
                window.setTimeout(() => {
                    speakNextAnnouncement();
                }, 180);
            };

            utterance.onend = finishCurrentAnnouncement;
            utterance.onerror = finishCurrentAnnouncement;
            isSpeakingRef.current = true;
            activeUtteranceRef.current = utterance;
            window.speechSynthesis.resume?.();

            const estimatedSpeechMs = Math.max(2500, message.length * (speechLanguage === 'ro-RO' ? 95 : 80));
            speechWatchdogTimerRef.current = setTimeout(() => {
                finishCurrentAnnouncement();
            }, estimatedSpeechMs + 2500);

            window.speechSynthesis.speak(utterance);

            return true;
        } catch {
            activeUtteranceRef.current = null;
            isSpeakingRef.current = false;
            return false;
        }
    }, [getPreferredVoice, getSpeechLanguage]);

    const announceTimerEvent = useCallback((message: string, options: { tone?: 'countdown' | 'complete' | 'overtime'; voice?: boolean } = {}) => {
        const shouldPlayTone = options.tone !== undefined && isSoundEnabled;

        if (shouldPlayTone) {
            playNotificationTone(options.tone);
        }

        if (!isVoiceEnabled || options.voice === false) return;

        speechQueueRef.current.push(message);
        const didQueueSpeech = speakNextAnnouncement();

        if (!didQueueSpeech && isSoundEnabled && !shouldPlayTone) {
            if (fallbackToneTimerRef.current) clearTimeout(fallbackToneTimerRef.current);
            fallbackToneTimerRef.current = setTimeout(() => {
                playNotificationTone('complete');
                fallbackToneTimerRef.current = null;
            }, 120);
        }
    }, [isSoundEnabled, isVoiceEnabled, playNotificationTone, speakNextAnnouncement]);

    const clearSpeechQueue = useCallback(() => {
        speechQueueRef.current = [];
        isSpeakingRef.current = false;
        activeUtteranceRef.current = null;
        if (speechWatchdogTimerRef.current) {
            clearTimeout(speechWatchdogTimerRef.current);
            speechWatchdogTimerRef.current = null;
        }
        if (fallbackToneTimerRef.current) {
            clearTimeout(fallbackToneTimerRef.current);
            fallbackToneTimerRef.current = null;
        }
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
        }
    }, []);

    const resetAnnouncementMilestones = () => {
        announcedRemainingMilestonesRef.current = new Set();
        announcedOvertimeMinutesRef.current = new Set();
    };

    useEffect(() => {
        return () => {
            clearSpeechQueue();
        };
    }, [clearSpeechQueue]);

    const handleSubtimerComplete = (
        recordedElapsedTime = targetTime,
        completionMethod: 'automatic' | 'manual' = 'automatic'
    ) => {
        if (!currentSubtimer) return;

        const completedStepRecords = {
            ...sessionStepRecordsRef.current,
            [currentSubtimer.id]: { actualTime: recordedElapsedTime, completionMethod },
        };
        sessionStepRecordsRef.current = completedStepRecords;

        const updatedSubtimers = subtimers.map((subtimer, index) => {
            if (index === currentSubtimerIndex) {
                const newHistory = [...(subtimer.timeHistory || []), recordedElapsedTime];
                return { ...subtimer, timeHistory: newHistory, completed: true };
            }
            return subtimer;
        });

        if (currentSubtimerIndex < subtimers.length - 1) {
            const nextIndex = currentSubtimerIndex + 1;
            const nextSubtimer = subtimers[nextIndex];
            announceTimerEvent(`${t('timerElement.timeUp', { step: currentSubtimer.text })}. ${t('timerElement.movingToStep', { step: nextSubtimer?.text || t('timerElement.nextStepFallback') })}`, { tone: 'complete' });
            updateElement({
                ...element,
                subtimers: updatedSubtimers,
            });
            setCurrentSubtimerIndex(nextIndex);
            setElapsedTime(0);
            setPausedTime(0);
            setHasStartedCurrentStep(isPlaying);
        } else {
            setIsPlaying(false);
            setHasStartedCurrentStep(false);
            announceTimerEvent(`${t('timerElement.timeUp', { step: currentSubtimer.text })}. ${t('timerElement.routineComplete')}`, { tone: 'complete' });
            const today = new Date().toISOString().split('T')[0];
            const updatedCompletedDates = element.completedDates?.includes(today)
                ? element.completedDates
                : [...(element.completedDates || []), today];
            const completedAt = new Date().toISOString();
            const sessionSteps = updatedSubtimers.map((subtimer) => {
                const stepRecord = completedStepRecords[subtimer.id];
                return {
                    subtimerId: subtimer.id,
                    text: subtimer.text,
                    plannedTime: subtimer.targetTime,
                    actualTime: stepRecord?.actualTime ?? 0,
                    completionMethod: stepRecord?.completionMethod || 'skipped' as const,
                };
            });
            const plannedTime = sessionSteps.reduce((total, step) => total + step.plannedTime, 0);
            const actualTime = sessionSteps.reduce((total, step) => total + step.actualTime, 0);

            updateElement({
                ...element,
                subtimers: updatedSubtimers,
                completedDates: updatedCompletedDates,
                sessionHistory: [
                    ...(element.sessionHistory || []),
                    {
                        id: generateId(),
                        startedAt: sessionStartedAtRef.current || completedAt,
                        completedAt,
                        completed: true,
                        pausedTime: sessionPausedTimeRef.current,
                        plannedTime,
                        actualTime,
                        overtime: sessionSteps.reduce(
                            (total, step) => total + Math.max(step.actualTime - step.plannedTime, 0),
                            0
                        ),
                        steps: sessionSteps,
                    },
                ],
            });
            setCurrentSubtimerIndex(currentSubtimerIndex);
            setElapsedTime(targetTime);
            setPausedTime(0);
            sessionStartedAtRef.current = null;
            sessionPausedTimeRef.current = 0;
            sessionStepRecordsRef.current = {};
            resetAnnouncementMilestones();
        }
    };

    handleSubtimerCompleteRef.current = handleSubtimerComplete;

    useEffect(() => {
        announcedRemainingMilestonesRef.current = new Set();
        announcedOvertimeMinutesRef.current = new Set();
    }, [currentSubtimerIndex, currentSubtimer?.id]);

    useEffect(() => {
        if (isPlaying) {
            timerRef.current = setInterval(() => {
                setElapsedTime(prev => shouldCountOvertime ? prev + 1 : Math.min(prev + 1, targetTime));
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isPlaying, currentSubtimerIndex, element.subtimers, targetTime, shouldCountOvertime]);

    useEffect(() => {
        if (!isPlaying && hasStartedCurrentStep && currentSubtimer && !isRoutineComplete) {
            pauseTimerRef.current = setInterval(() => {
                setPausedTime(prev => prev + 1);
                sessionPausedTimeRef.current += 1;
            }, 1000);
        } else if (pauseTimerRef.current) {
            clearInterval(pauseTimerRef.current);
        }

        return () => {
            if (pauseTimerRef.current) {
                clearInterval(pauseTimerRef.current);
            }
        };
    }, [isPlaying, hasStartedCurrentStep, currentSubtimer, isRoutineComplete]);

    useEffect(() => {
        if (isPlaying && !shouldCountOvertime && elapsedTime >= targetTime) {
            handleSubtimerCompleteRef.current?.();
        }
    }, [elapsedTime, isPlaying, targetTime, shouldCountOvertime]);

    useEffect(() => {
        if (!isPlaying || !currentSubtimer) return;

        const countdownMilestones = [...new Set([
            targetTime,
            Math.floor(targetTime / 2),
            30,
            Math.floor(targetTime / 4),
            10,
            5,
        ])].filter((milestone) => milestone > 0 && milestone <= targetTime);
        const milestoneToAnnounce = countdownMilestones.find((milestone) => {
            if (milestone > targetTime) return false;
            return (
                remainingTime === milestone &&
                !announcedRemainingMilestonesRef.current.has(milestone)
            );
        });

        if (milestoneToAnnounce !== undefined) {
            announcedRemainingMilestonesRef.current.add(milestoneToAnnounce);
            announceTimerEvent(t('timerElement.secondsRemaining', {
                count: milestoneToAnnounce,
                step: currentSubtimer.text,
            }), {
                tone: 'countdown',
                voice: milestoneToAnnounce !== targetTime,
            });
        }

        if (shouldCountOvertime && elapsedTime >= targetTime && !announcedRemainingMilestonesRef.current.has(0)) {
            announcedRemainingMilestonesRef.current.add(0);
            announceTimerEvent(t('timerElement.timeUp', { step: currentSubtimer.text }), { tone: 'overtime' });
        }

        if (overtime > 0 && overtime % 60 === 0) {
            const overtimeMinute = overtime / 60;
            if (!announcedOvertimeMinutesRef.current.has(overtimeMinute)) {
                announcedOvertimeMinutesRef.current.add(overtimeMinute);
                announceTimerEvent(t('timerElement.overtimeMinute', {
                    count: overtimeMinute,
                    step: currentSubtimer.text,
                }), { tone: 'overtime' });
            }
        }
    }, [announceTimerEvent, currentSubtimer, elapsedTime, isPlaying, overtime, remainingTime, shouldCountOvertime, targetTime, t]);

    const handlePlayPause = () => {
        if (!currentSubtimer) return;

        if (!isPlaying && isRoutineComplete) {
            clearSpeechQueue();
            sessionStartedAtRef.current = null;
            sessionPausedTimeRef.current = 0;
            sessionStepRecordsRef.current = {};
            updateElement({
                ...element,
                subtimers: subtimers.map((subtimer) => ({ ...subtimer, completed: false })),
            });
            setCurrentSubtimerIndex(0);
            setElapsedTime(0);
            setPausedTime(0);
        }

        if (!isPlaying) {
            if (!sessionStartedAtRef.current) {
                sessionStartedAtRef.current = new Date().toISOString();
            }
            setHasStartedCurrentStep(true);
        }

        setIsPlaying(!isPlaying);
    };

    const handleReset = () => {
        const resetAt = new Date().toISOString();
        const currentSessionRecords = { ...sessionStepRecordsRef.current };
        if (currentSubtimer && elapsedTime > 0) {
            currentSessionRecords[currentSubtimer.id] = {
                actualTime: elapsedTime,
                completionMethod: 'manual',
            };
        }
        const abandonedSteps = subtimers.map((subtimer) => {
            const stepRecord = currentSessionRecords[subtimer.id];
            return {
                subtimerId: subtimer.id,
                text: subtimer.text,
                plannedTime: subtimer.targetTime,
                actualTime: stepRecord?.actualTime ?? 0,
                completionMethod: stepRecord
                    ? (subtimer.id === currentSubtimer?.id && !subtimer.completed ? 'incomplete' as const : stepRecord.completionMethod)
                    : 'skipped' as const,
            };
        });
        const abandonedActualTime = abandonedSteps.reduce((total, step) => total + step.actualTime, 0);
        const shouldRecordAbandonedSession = Boolean(
            sessionStartedAtRef.current && (abandonedActualTime > 0 || sessionPausedTimeRef.current > 0)
        );

        setIsPlaying(false);
        setHasStartedCurrentStep(false);
        setCurrentSubtimerIndex(0);
        setElapsedTime(0);
        setPausedTime(0);
        resetAnnouncementMilestones();
        clearSpeechQueue();
        updateElement({
            ...element,
            subtimers: subtimers.map((subtimer) => ({ ...subtimer, completed: false })),
            sessionHistory: shouldRecordAbandonedSession
                ? [
                    ...(element.sessionHistory || []),
                    {
                        id: generateId(),
                        startedAt: sessionStartedAtRef.current || resetAt,
                        completedAt: resetAt,
                        completed: false,
                        pausedTime: sessionPausedTimeRef.current,
                        plannedTime: abandonedSteps.reduce((total, step) => total + step.plannedTime, 0),
                        actualTime: abandonedActualTime,
                        overtime: abandonedSteps.reduce(
                            (total, step) => total + Math.max(step.actualTime - step.plannedTime, 0),
                            0
                        ),
                        steps: abandonedSteps,
                    },
                ]
                : element.sessionHistory,
        });
        sessionStartedAtRef.current = null;
        sessionPausedTimeRef.current = 0;
        sessionStepRecordsRef.current = {};
    };

    const handleSelectSubtimer = (index: number) => {
        setCurrentSubtimerIndex(index);
        setElapsedTime(0);
        setPausedTime(0);
        setHasStartedCurrentStep(false);
        resetAnnouncementMilestones();
        clearSpeechQueue();
        setIsPlaying(false);
    };

    const handleCompleteCurrentSubtimer = () => {
        handleSubtimerComplete(
            shouldCountOvertime ? Math.max(elapsedTime, targetTime) : Math.min(elapsedTime, targetTime),
            'manual'
        );
    };

    const handleToggleSubtimerAutoMove = (index: number) => {
        const updatedSubtimers = subtimers.map((subtimer, subtimerIndex) => {
            if (subtimerIndex !== index) return subtimer;
            return {
                ...subtimer,
                autoMoveNext: !getSubtimerAutoMove(subtimer),
            };
        });

        updateElement({
            ...element,
            subtimers: updatedSubtimers,
        });
    };

    const handleToggleVoice = () => {
        if (isVoiceEnabled) {
            clearSpeechQueue();
        }

        updateElement({
            ...element,
            voiceEnabled: !isVoiceEnabled,
        });
    };

    const handleToggleSound = () => {
        if (isSoundEnabled && fallbackToneTimerRef.current) {
            clearTimeout(fallbackToneTimerRef.current);
            fallbackToneTimerRef.current = null;
        }

        updateElement({
            ...element,
            soundEnabled: !isSoundEnabled,
        });
    };

    const handleEdit = () => {
        setEditTitle(element.title || '');
        setEditMode(element.mode === 'simple' ? 'simple' : 'steps');
        setEditSubtimers(element.subtimers || []);
        setShowEditDialog(true);
    };

    const handleSaveEdit = () => {
        const simpleSubtimer = editSubtimers[0] || {
            id: generateId(),
            text: editTitle,
            targetTime: 60,
            timeHistory: [],
            completed: false,
        };
        updateElement({
            ...element,
            title: editTitle,
            mode: editMode,
            subtimers: editMode === 'simple'
                ? [{ ...simpleSubtimer, text: editTitle }]
                : editSubtimers,
        });
        setShowEditDialog(false);
    };

    const handleDelete = () => {
        deleteElement(element.id);
        setShowDeleteConfirmationDialog(false);
    };

    const handleAddSubtimer = () => {
        if (newSubtimer.trim()) {
            setEditSubtimers([...editSubtimers, { 
                id: generateId(), 
                text: newSubtimer.trim(), 
                targetTime: newTargetTime, 
                timeHistory: [],
                completed: false
            }]);
            setNewSubtimer('');
            setNewTargetTime(60);
        }
    };

    const handleRemoveSubtimer = (index: number) => {
        setEditSubtimers(editSubtimers.filter((_, i) => i !== index));
    };

    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return hours > 0
            ? `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
            : `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const stepHistory = subtimers.flatMap((subtimer) =>
        (subtimer.timeHistory || []).map((actualTime) => ({
            actualTime,
            plannedTime: subtimer.targetTime,
        }))
    );
    const completedFocusedTime = stepHistory.reduce((total, record) => total + record.actualTime, 0);
    const abandonedFocusedTime = (element.sessionHistory || [])
        .filter((session) => session.completed === false)
        .reduce((total, session) => total + session.actualTime, 0);
    const totalFocusedTime = completedFocusedTime + abandonedFocusedTime;
    const totalOvertime = stepHistory.reduce(
        (total, record) => total + Math.max(record.actualTime - record.plannedTime, 0),
        0
    );
    const averageOvertime = stepHistory.length > 0 ? Math.round(totalOvertime / stepHistory.length) : 0;
    const onTimeCount = stepHistory.filter((record) => record.actualTime <= record.plannedTime).length;
    const onTimeRate = stepHistory.length > 0 ? Math.round((onTimeCount / stepHistory.length) * 100) : 0;
    const completedSessionCount = (element.sessionHistory || []).filter((session) => session.completed !== false).length;
    const recordedRunCount = Math.max(
        completedSessionCount,
        element.completedDates?.length || 0,
        ...subtimers.map((subtimer) => subtimer.timeHistory?.length || 0),
        0
    );
    const startOfWeek = new Date();
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - ((startOfWeek.getDay() + 6) % 7));
    const activeDates = new Set([
        ...(element.completedDates || []),
        ...(element.sessionHistory || []).map((session) => session.completedAt.slice(0, 10)),
    ]);
    const activeDaysThisWeek = [...activeDates].filter((date) => {
        const parsedDate = new Date(`${date}T00:00:00`);
        return !Number.isNaN(parsedDate.getTime()) && parsedDate >= startOfWeek;
    }).length;
    const orderedSessions = [...(element.sessionHistory || [])].sort(
        (a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
    );
    const formatSessionDate = (date: string) => new Intl.DateTimeFormat(
        i18n.language?.startsWith('ro') ? 'ro-RO' : 'en-US',
        { dateStyle: 'medium', timeStyle: 'short' }
    ).format(new Date(date));
    const formatDifference = (actualTime: number, plannedTime: number) => {
        const difference = actualTime - plannedTime;
        if (difference === 0) return t('timerElement.onTime');
        return difference > 0
            ? t('timerElement.overBy', { time: formatTime(difference) })
            : t('timerElement.aheadBy', { time: formatTime(Math.abs(difference)) });
    };

    const actions = [
        {
            label: t('timerElement.statistics'),
            icon: faChartColumn,
            callback: () => setShowStatsDialog(true),
        },
        {
            label: t('timerElement.editTimer'),
            icon: faPencil,
            callback: handleEdit,
        },
        {
            label: t('timerElement.moveToCollection'),
            icon: faArrowRight,
            callback: () => setShowListSelectionDialog(true),
        },
        ...(onMoveToSection ? [{
            label: t('sections.moveToSection'),
            icon: faLayerGroup,
            callback: () => onMoveToSection(element),
        }] : []),
        {
            label: t('timerElement.deleteTimer'),
            icon: faTrash,
            callback: () => setShowDeleteConfirmationDialog(true),
        },
    ];

    return (
        <div className="timer-element">
            <div className="timer-header">
                <div className="timer-header-left">
                    <FontAwesomeIcon icon={faClock} className="timer-header-clock" />
                    <div className="timer-header-text">
                        <span className="element-type-label timer-type-label">{t('elementTypes.timer')}</span>
                        <span className="timer-header-title">{element.title}</span>
                        {!isSimpleMode && <span className="timer-doing-task">{currentSubtimer?.text || t('timerElement.noSubtimer')}</span>}
                    </div>
                </div>
                <div className="timer-header-right">
                    <span className={`timer-main-time ${overtime > 0 ? 'overtime' : ''}`}>
                        {overtime > 0 ? `+${formatTime(displayTime)}` : formatTime(displayTime)}
                    </span>
                    <button onClick={handlePlayPause} className="timer-play-button" aria-label={isPlaying ? t('timerElement.pause') : t('timerElement.start')}>
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>
                    <button
                        onClick={handleCompleteCurrentSubtimer}
                        className="timer-complete-step-button active"
                        aria-label={t('timerElement.completeStep')}
                        disabled={!currentSubtimer}
                    >
                        <FontAwesomeIcon icon={faCheck} />
                    </button>
                    {isSimpleMode && (
                        <button
                            type="button"
                            onClick={() => handleToggleSubtimerAutoMove(0)}
                            className={`timer-audio-toggle timer-overtime-toggle ${shouldCountOvertime ? 'active' : ''}`}
                            aria-label={shouldCountOvertime ? t('timerElement.simpleOvertimeOn') : t('timerElement.simpleOvertimeOff')}
                            title={shouldCountOvertime ? t('timerElement.simpleOvertimeOn') : t('timerElement.simpleOvertimeOff')}
                        >
                            <FontAwesomeIcon icon={faInfinity} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleToggleVoice}
                        className={`timer-audio-toggle ${isVoiceEnabled ? 'active' : ''}`}
                        aria-label={isVoiceEnabled ? t('timerElement.voiceOn') : t('timerElement.voiceOff')}
                        title={isVoiceEnabled ? t('timerElement.voiceOn') : t('timerElement.voiceOff')}
                    >
                        <FontAwesomeIcon icon={isVoiceEnabled ? faVolumeHigh : faVolumeXmark} />
                    </button>
                    <button
                        type="button"
                        onClick={handleToggleSound}
                        className={`timer-audio-toggle ${isSoundEnabled ? 'active' : ''}`}
                        aria-label={isSoundEnabled ? t('timerElement.soundOn') : t('timerElement.soundOff')}
                        title={isSoundEnabled ? t('timerElement.soundOn') : t('timerElement.soundOff')}
                    >
                        <FontAwesomeIcon icon={faMusic} />
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="timer-reset-button"
                        aria-label={t('timerElement.reset')}
                        title={t('timerElement.reset')}
                    >
                        <FontAwesomeIcon icon={faRotateLeft} />
                    </button>
                    <ActionMenu actions={actions} icon={faEllipsisV} buttonClassName="timer-menu-button" buttonStyle={{}} />
                </div>
            </div>

            {!isSimpleMode && <div className="timer-progress-row">
                <div className="timer-progress-copy">
                    <span>{t('timerElement.stepProgress', { current: subtimers.length ? currentSubtimerIndex + 1 : 0, total: subtimers.length })}</span>
                    <span>
                        {!isPlaying && pausedTime > 0
                            ? t('timerElement.pausedFor', { time: formatTime(pausedTime) })
                            : t('timerElement.routineProgress', { percent: routineProgress })}
                    </span>
                </div>
                <div className="timer-progress-track" aria-label={t('timerElement.routineProgress', { percent: routineProgress })}>
                    <div className="timer-progress-fill" style={{ width: `${routineProgress}%` }} />
                </div>
            </div>}

            {!isSimpleMode && <div className="timer-subtimers-section">
                <button
                    type="button"
                    className="timer-subtimers-header"
                    onClick={() => setSubtimersCollapsed(!subtimersCollapsed)}
                    aria-expanded={!subtimersCollapsed}
                >
                    <FontAwesomeIcon 
                        icon={subtimersCollapsed ? faChevronRight : faChevronDown} 
                        className="subtimers-collapse-icon"
                    />
                    <span className="subtimers-title">{t('timerElement.subTimers')} ({completedCount}/{subtimers.length})</span>
                </button>
                {!subtimersCollapsed && (
                    <>
                        <div className="timer-subtimers-list">
                            {subtimers.map((subtimer, index) => {
                                const isCompleted = subtimer.completed || index < currentSubtimerIndex;
                                const isActive = index === currentSubtimerIndex;
                                const autoMoveNext = getSubtimerAutoMove(subtimer);
                                return (
                                <div 
                                    key={subtimer.id} 
                                    className={`timer-subtimer-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                    onClick={() => handleSelectSubtimer(index)}
                                >
                                    <div className={`timer-subtimer-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                                        {isCompleted && <FontAwesomeIcon icon={faCheck} />}
                                    </div>
                                    <div className="timer-subtimer-info">
                                        <div className="timer-subtimer-text">{subtimer.text}</div>
                                        <div className="timer-subtimer-time">{formatTime(subtimer.targetTime)}</div>
                                    </div>
                                    <button
                                        className={`timer-auto-move-button ${autoMoveNext ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleSubtimerAutoMove(index);
                                        }}
                                        aria-label={autoMoveNext ? t('timerElement.autoMoveOn') : t('timerElement.autoMoveOff')}
                                        title={autoMoveNext ? t('timerElement.autoMoveOn') : t('timerElement.autoMoveOff')}
                                    >
                                        <FontAwesomeIcon icon={faForwardStep} />
                                    </button>
                                </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>}

            {showDeleteConfirmationDialog && (
                <ConfirmationDialog
                    title={t('timerElement.deleteTimerTitle')}
                    message={t('timerElement.deleteTimerConfirm')}
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteConfirmationDialog(false)}
                    isDanger={true}
                />
            )}
            {showListSelectionDialog && (
                <ListSelectionDialog
                    lists={lists}
                    selectedListId={selectedListId}
                    onSelectList={(listId: string) => {
                        moveElementToList(element, listId);
                        setShowListSelectionDialog(false);
                    }}
                    onClose={() => setShowListSelectionDialog(false)}
                />
            )}
            {showStatsDialog && (
                <div className="timer-edit-overlay" onClick={(e) => { e.stopPropagation(); setShowStatsDialog(false); }}>
                    <div className="timer-stats-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="timer-edit-dialog-header">
                            <div>
                                <h3>{t('timerElement.statistics')}</h3>
                                <p>{element.title}</p>
                            </div>
                            <button
                                type="button"
                                className="timer-edit-close-button"
                                onClick={() => setShowStatsDialog(false)}
                                aria-label={t('timerElement.closeStatistics')}
                                title={t('timerElement.closeStatistics')}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="timer-stats-tabs" role="tablist" aria-label={t('timerElement.statistics')}>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={statsView === 'overview'}
                                className={statsView === 'overview' ? 'active' : ''}
                                onClick={() => setStatsView('overview')}
                            >
                                {t('timerElement.overview')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={statsView === 'sessions'}
                                className={statsView === 'sessions' ? 'active' : ''}
                                onClick={() => setStatsView('sessions')}
                            >
                                {t('timerElement.sessions')}
                                {orderedSessions.length > 0 && <span>{orderedSessions.length}</span>}
                            </button>
                        </div>

                        {statsView === 'overview' ? (
                            <div role="tabpanel">
                                <div className="timer-stats-summary">
                                    <div className="timer-stat-item">
                                        <span>{t('timerElement.completedRuns')}</span>
                                        <strong>{recordedRunCount}</strong>
                                    </div>
                                    <div className="timer-stat-item">
                                        <span>{t('timerElement.activeDaysThisWeek')}</span>
                                        <strong>{activeDaysThisWeek}</strong>
                                    </div>
                                    <div className="timer-stat-item">
                                        <span>{t('timerElement.totalFocusedTime')}</span>
                                        <strong>{formatTime(totalFocusedTime)}</strong>
                                    </div>
                                    <div className="timer-stat-item">
                                        <span>{t('timerElement.onTimeRate')}</span>
                                        <strong>{onTimeRate}%</strong>
                                    </div>
                                </div>

                                <div className="timer-stats-section-heading">
                                    <h4>{t('timerElement.stepPerformance')}</h4>
                                    <span>{t('timerElement.averageOvertime')}: {formatTime(averageOvertime)}</span>
                                </div>
                                <div className="timer-stats-table" role="table" aria-label={t('timerElement.stepPerformance')}>
                                    <div className="timer-stats-table-row timer-stats-table-header" role="row">
                                        <span role="columnheader">{t('timerElement.step')}</span>
                                        <span role="columnheader">{t('timerElement.runs')}</span>
                                        <span role="columnheader">{t('timerElement.averageTime')}</span>
                                        <span role="columnheader">{t('timerElement.overtime')}</span>
                                    </div>
                                    {subtimers.map((subtimer) => {
                                        const history = subtimer.timeHistory || [];
                                        const averageTime = history.length > 0
                                            ? Math.round(history.reduce((total, time) => total + time, 0) / history.length)
                                            : 0;
                                        const stepOvertime = history.length > 0
                                            ? Math.round(history.reduce((total, time) => total + Math.max(time - subtimer.targetTime, 0), 0) / history.length)
                                            : 0;
                                        return (
                                            <div className="timer-stats-table-row" role="row" key={subtimer.id}>
                                                <span className="timer-stats-step-name" role="cell">{subtimer.text}</span>
                                                <span role="cell">{history.length}</span>
                                                <span role="cell">{history.length > 0 ? formatTime(averageTime) : '-'}</span>
                                                <span className={stepOvertime > 0 ? 'has-overtime' : ''} role="cell">
                                                    {history.length > 0 ? formatTime(stepOvertime) : '-'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {stepHistory.length === 0 && (
                                    <p className="timer-stats-empty">{t('timerElement.noStatisticsYet')}</p>
                                )}
                            </div>
                        ) : (
                            <div className="timer-session-list" role="tabpanel">
                                {orderedSessions.length === 0 ? (
                                    <div className="timer-session-empty">
                                        <FontAwesomeIcon icon={faClock} />
                                        <p>{t('timerElement.noSessionsYet')}</p>
                                    </div>
                                ) : orderedSessions.map((session) => {
                                    const sessionId = session.id || session.completedAt;
                                    const isExpanded = expandedSessionId === sessionId;
                                    const sessionStatus = session.completed === false
                                        ? 'abandoned'
                                        : session.actualTime > session.plannedTime
                                            ? 'overtime'
                                            : session.actualTime < session.plannedTime
                                                ? 'ahead'
                                                : 'on-time';
                                    return (
                                        <div className={`timer-session-item ${isExpanded ? 'expanded' : ''}`} key={sessionId}>
                                            <button
                                                type="button"
                                                className="timer-session-trigger"
                                                aria-expanded={isExpanded}
                                                onClick={() => setExpandedSessionId(isExpanded ? null : sessionId)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                    className="timer-session-chevron"
                                                />
                                                <span className="timer-session-identity">
                                                    <strong>{formatSessionDate(session.completedAt)}</strong>
                                                    <small>
                                                        {t('timerElement.actualVsPlanned', {
                                                            actual: formatTime(session.actualTime),
                                                            planned: formatTime(session.plannedTime),
                                                        })}
                                                    </small>
                                                </span>
                                                <span className={`timer-session-result ${sessionStatus}`}>
                                                    {session.completed === false
                                                        ? t('timerElement.abandoned')
                                                        : formatDifference(session.actualTime, session.plannedTime)}
                                                </span>
                                            </button>

                                            {isExpanded && (
                                                <div className="timer-session-details">
                                                    <div className="timer-session-meta">
                                                        <span>
                                                            {t('timerElement.started')}
                                                            <strong>{session.startedAt ? formatSessionDate(session.startedAt) : '-'}</strong>
                                                        </span>
                                                        <span>
                                                            {t('timerElement.paused')}
                                                            <strong>{formatTime(session.pausedTime || 0)}</strong>
                                                        </span>
                                                        <span>
                                                            {t('timerElement.focused')}
                                                            <strong>{formatTime(session.actualTime)}</strong>
                                                        </span>
                                                    </div>
                                                    <div className="timer-session-steps">
                                                        {session.steps.map((step, index) => {
                                                            const method = step.completionMethod || 'automatic';
                                                            const stepStatus = method === 'skipped' || method === 'incomplete'
                                                                ? method
                                                                : step.actualTime > step.plannedTime
                                                                    ? 'overtime'
                                                                    : step.actualTime < step.plannedTime
                                                                        ? 'ahead'
                                                                        : 'on-time';
                                                            return (
                                                                <div className="timer-session-step" key={`${step.subtimerId}-${index}`}>
                                                                    <span className="timer-session-step-name">
                                                                        <strong>{step.text}</strong>
                                                                        <small>{t(`timerElement.method.${method}`)}</small>
                                                                    </span>
                                                                    <span>
                                                                        <small>{t('timerElement.planned')}</small>
                                                                        <strong>{formatTime(step.plannedTime)}</strong>
                                                                    </span>
                                                                    <span>
                                                                        <small>{t('timerElement.actual')}</small>
                                                                        <strong>{method === 'skipped' ? '-' : formatTime(step.actualTime)}</strong>
                                                                    </span>
                                                                    <span className={`timer-session-step-result ${stepStatus}`}>
                                                                        {method === 'skipped' || method === 'incomplete'
                                                                            ? t(`timerElement.${method}`)
                                                                            : formatDifference(step.actualTime, step.plannedTime)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {showEditDialog && (
                <div className="timer-edit-overlay" onClick={(e) => { e.stopPropagation(); setShowEditDialog(false); }}>
                    <div className="timer-edit-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="timer-edit-dialog-header">
                            <div>
                                <h3>{t('timerElement.editTimer')}</h3>
                                <p>{editMode === 'simple' ? t('timerElement.simple') : `${t('timerElement.subtimers')} ${editSubtimers.length}`}</p>
                            </div>
                            <button
                                type="button"
                                className="timer-edit-close-button"
                                onClick={() => setShowEditDialog(false)}
                                aria-label={t('timerElement.cancel')}
                                title={t('timerElement.cancel')}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="timer-edit-field">
                            <label>{t('timerElement.timerName')}</label>
                            <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="timer-edit-input"
                            />
                        </div>
                        <div className="element-mode-selector" role="group" aria-label={t('timerElement.mode')}>
                            <button type="button" className={editMode === 'simple' ? 'active' : ''} onClick={() => setEditMode('simple')}>{t('timerElement.simple')}</button>
                            <button type="button" className={editMode === 'steps' ? 'active' : ''} onClick={() => setEditMode('steps')}>{t('timerElement.withSteps')}</button>
                        </div>
                        {editMode === 'simple' ? (
                            <div className="simple-duration-field">
                                <label>{t('timerElement.duration')}</label>
                                <TimePicker
                                    value={editSubtimers[0]?.targetTime || 60}
                                    onChange={(value: number) => setEditSubtimers((current) => current.length > 0
                                        ? current.map((subtimer, index) => index === 0 ? { ...subtimer, targetTime: value } : subtimer)
                                        : [{ id: generateId(), text: editTitle, targetTime: value, timeHistory: [], completed: false }]
                                    )}
                                    variant="popover"
                                />
                            </div>
                        ) : <>
                        <div className="timer-edit-subtimers">
                            <div className="timer-edit-section-heading">
                                <label>{t('timerElement.subtimers')}</label>
                                <span>{editSubtimers.length}</span>
                            </div>
                            {editSubtimers.map((subtimer, index) => (
                                <div key={subtimer.id} className="timer-edit-subtimer-item">
                                    <span className="timer-edit-subtimer-number">{index + 1}</span>
                                    <input
                                        type="text"
                                        value={subtimer.text}
                                        onChange={(e) => {
                                            const updated = editSubtimers.map((s, i) => 
                                                i === index ? { ...s, text: e.target.value } : s
                                            );
                                            setEditSubtimers(updated);
                                        }}
                                        className="timer-edit-subtimer-input"
                                    />
                                    <TimePicker
                                        value={subtimer.targetTime}
                                        onChange={(val: number) => {
                                            const updated = editSubtimers.map((s, i) => 
                                                i === index ? { ...s, targetTime: val } : s
                                            );
                                            setEditSubtimers(updated);
                                        }}
                                        variant="popover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSubtimer(index)}
                                        className="timer-remove-subtimer"
                                        aria-label={t('timerElement.removeSubtimer')}
                                        title={t('timerElement.removeSubtimer')}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="timer-add-subtimer">
                            <span className="timer-edit-subtimer-number timer-edit-subtimer-number-add">
                                <FontAwesomeIcon icon={faPlus} />
                            </span>
                            <div className="timer-add-subtimer-controls">
                                <input
                                    type="text"
                                    value={newSubtimer}
                                    onChange={(e) => setNewSubtimer(e.target.value)}
                                    placeholder={t('timerElement.addSubtimer')}
                                    className="timer-edit-subtimer-input"
                                />
                                <TimePicker
                                    value={newTargetTime}
                                    onChange={(val: number) => setNewTargetTime(val)}
                                    variant="popover"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleAddSubtimer}
                                className="timer-add-subtimer-button"
                                aria-label={t('timerElement.addSubTimer')}
                                title={t('timerElement.addSubTimer')}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </button>
                        </div>
                        </>}
                        <div className="timer-edit-actions">
                            <button onClick={() => setShowEditDialog(false)}>{t('timerElement.cancel')}</button>
                            <button onClick={handleSaveEdit} className="primary">{t('timerElement.save')}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
